import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { LinkButton } from '@clds/button';
import { fromAccountId } from '@cld/customer-logo';
import { getAnalyticsProp, useAnalytics } from '@cld/analytics';
import { useApplicationWrapperContext } from '../../context/applicationWrapper/ApplicationWrapper.context';
import { useApplicationsDropdownWrapperContext } from '../../context/applicationDropdownWrapper/ApplicationsDropdownWrapper.context';
import { ApplicationInfo } from '../../ApplicationWrapper.types';
import { messages } from '../../i18n';
import { ErrorRefresh } from '../../components/ErrorRefresh/ErrorRefresh';
import { FetchStatus } from '../../api/api.types';
import { ApplicationSvgIcon } from '../DefaultApplicationsDropdown';
import { CloudLogo } from '../../components';
import { applicationsInfo, productExplorerApplicationInfo } from '../../api/productList/ApplicationsInfoMap.const';
import { useCustomerIdContext, useUserData } from '../../context';
import { CLD_LOGO_IMG_NAME } from '../../ApplicationWrapper.const';
import Popover from '@clds/popover';
import { DescriptionPopoverContent } from './DescriptionPopoverContent';
import { UserRole, AppId, ApplicationsListCategories } from '@cld/application-wrapper-common';
import { useVideoAppAccess } from '../../useVideoAppAccess';
import { useUnicornApiContext } from '../../UnicornApi.context';

// hide second Programmable Media link if both are present - todo remove after switch to new PM app
const StyledLinkButton = styled(LinkButton)<{ isSelected?: boolean }>`
  position: relative;

  &[data-appid='media_api'] ~ &[data-appid='media_api'] {
    display: none;
  }

  .icon-active,
  .icon-inactive {
    transition: opacity 0.2s;
  }

  .icon-inactive {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
  }

  .icon-active {
    opacity: 0;
  }

  :hover {
    .icon-active {
      opacity: 1;
    }

    .icon-inactive {
      opacity: 0;
    }
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      .icon-active {
        opacity: 1;
      }

      .icon-inactive {
        opacity: 0;
      }
    `}
`;

export interface ProductsListProps {
  showVideoAppSelector?: boolean;
}

// istanbul ignore next
export const ProductsList: FC<ProductsListProps> = ({ showVideoAppSelector }) => {
  const { selectedAppId } = useApplicationWrapperContext();
  const { currentCustomer } = useCustomerIdContext();
  const { applicationGroupsList, applicationListFetchStatus, onAppSelect } = useApplicationsDropdownWrapperContext();
  const analytics = useAnalytics('products menu');
  const { formatMessage } = useIntl();
  const { userData } = useUserData();
  const { unicornApi } = useUnicornApiContext();
  const analyticsProp = getAnalyticsProp('company logo');
  const isVideoAppSelectorVisible = useVideoAppAccess(!!showVideoAppSelector);

  const onClick = (item: ApplicationInfo, event: MouseEvent) => {
    analytics(`switch to ${item.name}`, 'switch product');

    if (!item.openInNewWindow) {
      sessionStorage.setItem('selectedApp', item.appId);
      onAppSelect?.(item.appId, event);
    }
  };

  // FIXME: only use unicornApi once console integrates, remove the alternative case completely
  const theme = useMemo(() => {
    if (unicornApi) {
      return unicornApi.data.theme.name;
    }

    // If unicornApi is unavailable, it means we are in DAM as that is the only place without integration
    // We currently need a copy of Unicorn logic to decide on the theme in that case
    if (userData?.userTheme === 'dark') {
      return 'dark';
    }

    if (userData?.userTheme === 'light') {
      return 'light';
    }

    if (userData?.userTheme === 'system') {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }

      return 'light';
    }

    return 'light';
  }, [unicornApi, userData]);

  // override navigation if new Programmable Media link is to be displayed - todo remove after switch to new PM app
  const renderApplicationItem = (item: ApplicationInfo) => (
    <Popover
      paperProps={{ isPadded: false }}
      key={item.appId}
      overlay={
        <DescriptionPopoverContent
          onClick={(event) => onClick(item, event.nativeEvent)}
          isBeta={item.isBeta}
          isEarlyAccess={item.isEarlyAccess}
          heading={formatMessage(messages[item.name])}
          openInNewWindow={item.openInNewWindow}
          subheading={formatMessage(messages[item.description])}
          href={item.href({ customerExternalId: currentCustomer?.externalId ?? '' })}
        />
      }
      trigger="hover"
      placement="right"
      motion={{ motionEnter: false, motionLeave: false }}
    >
      <StyledLinkButton
        href={item.href({ customerExternalId: currentCustomer?.externalId ?? '' })}
        id={item.appId}
        isSelected={selectedAppId === item?.appId}
        leftSlot={
          <>
            <ApplicationSvgIcon className="icon-active" size="md" svg={item.iconActive} />

            {theme === 'light' ? (
              <ApplicationSvgIcon className="icon-inactive" size="md" svg={item.iconInactiveLight} />
            ) : (
              <ApplicationSvgIcon className="icon-inactive" size="md" svg={item.iconInactiveDark} />
            )}
          </>
        }
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => onClick(item, event.nativeEvent)}
        tone="plain"
        variant="secondary"
        data-appid={item.appId}
        target={item.openInNewWindow ? '_blank' : undefined}
      />
    </Popover>
  );

  const { allProducts } = useMemo(() => {
    const mediaApplications = applicationGroupsList?.[ApplicationsListCategories.Media];
    const programmableMediaApplications = applicationGroupsList?.[ApplicationsListCategories.ProgrammableMedia];
    const videoApplication =
      mediaApplications?.find(({ appId }) => appId === AppId.MediaAPI) && isVideoAppSelectorVisible ? [applicationsInfo[AppId.Video]] : [];
    const mediaFlowsItem =
      userData && [UserRole.MasterAdmin, UserRole.Admin, UserRole.TechnicalAdmin].includes(userData.role) ? [applicationsInfo[AppId.MediaFlows]] : [];

    const dimensionsItem =
      userData && [UserRole.MasterAdmin, UserRole.Admin, UserRole.TechnicalAdmin].includes(userData.role) ? [applicationsInfo[AppId.Dimensions]] : [];

    const FinalTouchItem =
      userData && [UserRole.MasterAdmin, UserRole.Admin, UserRole.TechnicalAdmin].includes(userData.role) ? [applicationsInfo[AppId.FinalTouch]] : [];

    return {
      allProducts: [
        ...(programmableMediaApplications ?? []),
        ...(mediaApplications ?? []),
        ...videoApplication,
        ...mediaFlowsItem,
        ...dimensionsItem,
        ...FinalTouchItem,
      ],
    };
  }, [applicationGroupsList, userData, isVideoAppSelectorVisible]);

  const showProductExplorer = useMemo(
    () => (userData?.role ? [UserRole.Admin, UserRole.MasterAdmin, UserRole.TechnicalAdmin].includes(userData.role) : false),
    [userData]
  );

  return applicationListFetchStatus === FetchStatus.Failure ? (
    <ErrorRefresh message={formatMessage(messages.cantAccessYourProducts)} />
  ) : (
    <>
      {showProductExplorer && currentCustomer ? (
        <LinkButton
          id={productExplorerApplicationInfo.appId}
          href={productExplorerApplicationInfo.href({ customerExternalId: currentCustomer.externalId })}
          leftSlot={<CloudLogo src={fromAccountId(userData?.userInfo?.accountId, CLD_LOGO_IMG_NAME)} />}
          onClick={(event: { nativeEvent: MouseEvent | undefined; preventDefault: () => void }) => {
            onAppSelect?.(productExplorerApplicationInfo.appId, event.nativeEvent);
            event.preventDefault();
          }}
          {...analyticsProp}
        />
      ) : (
        <CloudLogo src={fromAccountId(userData?.userInfo?.accountId, CLD_LOGO_IMG_NAME)} {...analyticsProp} />
      )}

      {allProducts.map((product) => renderApplicationItem(product))}
    </>
  );
};
